/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Typography, Input, Row, Col, Select, Spin, message, Modal, Upload, Checkbox as AntCheckbox } from 'antd';
import { BarChartOutlined, SaveOutlined, PlusOutlined, DeleteOutlined, CheckOutlined, DownloadOutlined, UploadOutlined, EditOutlined } from '@ant-design/icons';
import { BarChart2, Plus, X, ArrowRight, CheckCircle, AlertCircle, XCircle, Trash2, Edit, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Button as CrcButton, Checkbox, Header, Toggle, Spinner, ConfirmationModal } from '@crc-evans/ui-components';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import axiosConfig from '../../services/axiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const CustomWelderSelect = ({ value, options, onChange, placeholder = "Select Welders" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = (options || []).filter(opt =>
    (opt.welderId || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleValue = (val) => {
    let newValues;
    if (selectedValues.includes(val)) {
      newValues = selectedValues.filter(v => v !== val);
    } else {
      newValues = [...selectedValues, val];
    }
    onChange(newValues);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', textAlign: 'left' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          minHeight: '42px',
          padding: '4px 12px',
          borderRadius: '8px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #D1D5DB',
          backgroundColor: '#fff',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          boxShadow: isOpen ? '0 0 0 4px rgba(59, 130, 246, 0.1)' : 'none'
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1, overflow: 'hidden' }}>
          {selectedValues.length > 0 ? (
            selectedValues.map(val => (
              <span
                key={val}
                style={{
                  backgroundColor: '#EFF6FF',
                  color: '#1E40AF',
                  fontSize: '12px',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: '1px solid #DBEAFE',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {val}
                <X
                  size={12}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleValue(val);
                  }}
                />
              </span>
            ))
          ) : (
            <span style={{ color: '#9CA3AF', fontSize: '14px' }}>{placeholder}</span>
          )}
        </div>
        {isOpen ? <ChevronUp size={18} color="#3B82F6" /> : <ChevronDown size={18} color="#6B7280" />}
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(-8px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          <div style={{ position: 'relative' }}>
            <input
              autoFocus
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                height: '40px',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                fontSize: '14px',
                outline: 'none',
                color: '#1F2937',
                boxSizing: 'border-box',
                backgroundColor: '#fff'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          <div style={{ maxHeight: '240px', overflowY: 'auto', paddingRight: '4px' }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => (
                <div
                  key={opt.welderId}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleValue(opt.welderId);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    transition: 'all 0.1s',
                    marginBottom: '4px',
                    backgroundColor: selectedValues.includes(opt.welderId) ? '#F3F4F6' : 'transparent'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedValues.includes(opt.welderId) ? '#F3F4F6' : 'transparent'}
                >
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: selectedValues.includes(opt.welderId) ? 'none' : '1px solid #D1D5DB',
                    borderRadius: '4px',
                    backgroundColor: selectedValues.includes(opt.welderId) ? '#3B82F6' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    transition: 'all 0.1s'
                  }}>
                    {selectedValues.includes(opt.welderId) && (
                      <CheckOutlined style={{ color: 'white', fontSize: '10px' }} />
                    )}
                  </div>
                  <span style={{
                    fontSize: '14px',
                    color: '#374151',
                    fontWeight: selectedValues.includes(opt.welderId) ? 500 : 400
                  }}>
                    {opt.welderId}
                  </span>
                </div>
              ))
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>
                No welders found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ProductivityLog = ({
  productivityData,
  onSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId
}) => {
  const initialProductivityData = productivityData && typeof productivityData === 'object' ? productivityData : {};
  const [rtProgress, setRtProgress] = useState({
    firstRtWeld: '',
    lastRtWeld: '',
    totalRtAcceptedWelds: 0
  });
  const [testTypes, setTestTypes] = useState([]);
  const [weldLogRows, setWeldLogRows] = useState([]);
  const [originalWeldLogMetrics, setOriginalWeldLogMetrics] = useState([]);
  const [deletedRowIndices, setDeletedRowIndices] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [isLoadingPipeTally, setIsLoadingPipeTally] = useState(false);
  const [isLoadingWeldLog, setIsLoadingWeldLog] = useState(false);
  const dispatch = useDispatch();
  const skipProductivity = useSelector(state => {
    if (!sowId) return false;
    return state.dprReducer?.skipStatesBySection?.[sowId]?.skipProductivity || false;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [reportNo, setReportNo] = useState('');
  const [welderConfigs, setWelderConfigs] = useState([]);
  const [welderPersonnel, setWelderPersonnel] = useState([]);
  const [defectTypes, setDefectTypes] = useState([]);
  const [showAddRowInput, setShowAddRowInput] = useState(false);
  const [rowsToAdd, setRowsToAdd] = useState('');
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadFileList, setUploadFileList] = useState([]);

  // Add Productivity Modal State
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [newProductivity, setNewProductivity] = useState({
    firstJoint: '',
    lastJoint: ''
  });
  const [validationError, setValidationError] = useState('');



  // Edit View State
  const [editingRow, setEditingRow] = useState(null);

  const [weldEntryRows, setWeldEntryRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);


  const handleDownloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Daily Weld Log');
      const safeColumns = Array.isArray(columns) ? columns : [];
      const headerTitles = safeColumns
        .map((col) => {
          let title = '';
          if (typeof col.title === 'string') {
            title = col.title;
          } else if (col.title && col.title.props && col.title.props.children) {
            title = col.title.props.children;
          }
          return { title: String(title || '').trim(), dataIndex: col.dataIndex };
        })
        .filter((col) => col.dataIndex && col.title && col.title !== '#' && col.title.toLowerCase() !== 'action')
        .map((col) => col.title);

      worksheet.addRow(headerTitles);
      worksheet.getRow(1).font = { bold: true };
      worksheet.columns = headerTitles.map((title) => ({
        header: title,
        width: Math.max(12, String(title || '').length + 4)
      }));

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'daily_weld_log_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Template download error:', error);
      message.error('Failed to download template. Please try again.');
    }
  };

  const handleUploadExcel = () => {
    setIsUploadModalVisible(true);
  };

  const handleUploadModalCancel = () => {
    setIsUploadModalVisible(false);
    setUploadFileList([]);
  };

  const handleUploadExcelFile = async () => {
    if (uploadFileList.length === 0) {
      message.error('Please select an Excel file.');
      return;
    }

    const file = uploadFileList[0].originFileObj || uploadFileList[0];
    if (!file) {
      message.error('Invalid file. Please try again.');
      return;
    }

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (!rows || rows.length < 2) {
        message.error('The uploaded file has no data rows.');
        return;
      }

      const headers = rows[0].map((h) => String(h || '').trim());
      const headerToDataIndex = {};
      const safeColumns = Array.isArray(columns) ? columns : [];
      safeColumns.forEach((col) => {
        if (!col.dataIndex) return;
        const title = typeof col.title === 'string' ? col.title : '';
        if (title) {
          headerToDataIndex[title.toLowerCase()] = col.dataIndex;
        }
      });

      const dataRows = rows.slice(1).filter((row) => row.some((cell) => cell !== null && cell !== undefined && cell !== ''));
      const mappedRows = dataRows.map((row, index) => {
        const rowObj = {
          id: `weld-upload-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`
        };

        headers.forEach((header, colIndex) => {
          if (!header || header === '#') {
            return;
          }
          const dataIndex = headerToDataIndex[header.toLowerCase()];
          if (!dataIndex) return;
          const value = row[colIndex];
          rowObj[dataIndex] = value !== undefined && value !== null ? value : '';
        });

        return rowObj;
      });

      setWeldLogRows((prevRows) => [...prevRows, ...mappedRows]);
      message.success('Excel data imported successfully.');
      handleUploadModalCancel();
    } catch (error) {
      console.error('Excel upload error:', error);
      message.error('Failed to read the Excel file. Please try again.');
    }
  };

  // Fetch pipe tally data
  const fetchPipeTallyData = useCallback(async () => {
    if (!dprLogId) return;
    setIsLoadingPipeTally(true);
    try {
      const response = await axiosConfig.get(`/dpr-pipe-tally?dprLogId=${dprLogId}&shift=${shift}`);

      console.log('Pipe Tally API Response:', response);
      console.log('Response data:', response.data);

      let testTypesData = null;

      if (response.data && response.data.data) {
        testTypesData = response.data.data.testTypes;
      }

      // Store all testTypes
      if (testTypesData && Array.isArray(testTypesData) && testTypesData.length > 0) {
        setTestTypes(testTypesData);

        // Also update rtProgress for backward compatibility (find RT test type)
        const rtTestType = testTypesData.find(test => test.testType === 'RT');
        if (rtTestType) {
          setRtProgress({
            firstRtWeld: rtTestType.firstWeldNo || '',
            lastRtWeld: rtTestType.lastWeldNo || '',
            totalRtAcceptedWelds: rtTestType.totalCompleted || 0
          });
        }
      } else {
        setTestTypes([]);
      }
    } catch (error) {
      console.error('Error fetching pipe tally data:', error);
    } finally {
      setIsLoadingPipeTally(false);
    }
  }, [shift, dprLogId]);

  // Fetch productivity/weld log data
  const fetchProductivityData = useCallback(async () => {
    if (!dprLogId) return;
    setIsLoadingWeldLog(true);
    try {
      const response = await axiosConfig.get(`/dpr-productivity?dprLogId=${dprLogId}&shift=${shift}`);

      console.log('Productivity API Response:', response);
      console.log('Response data:', response.data);

      let weldLogMetricsData = null;
      let notesData = null;
      let welderConfigsData = null;
      let welderPersonnelData = null;
      let defectTypesData = null;

      if (response.data && response.data.data) {
        weldLogMetricsData = response.data.data.weldLogMetrics;
        notesData = response.data.data.notes;
        welderConfigsData = response.data.data.welderConfigs;
        welderPersonnelData = response.data.data.welderPersonnel;
        defectTypesData = response.data.data.defectTypes;
        if (response.data.data.reportNo) {
          setReportNo(response.data.data.reportNo);
        }
      }

      // Store welder configs from the response
      if (welderConfigsData && Array.isArray(welderConfigsData)) {
        setWelderConfigs(welderConfigsData);
      } else {
        setWelderConfigs([]);
      }

      // Store welder personnel from the response
      if (welderPersonnelData && Array.isArray(welderPersonnelData)) {
        setWelderPersonnel(welderPersonnelData);
      } else {
        setWelderPersonnel([]);
      }

      // Store defect types from the response
      if (defectTypesData && Array.isArray(defectTypesData)) {
        setDefectTypes(defectTypesData);
      } else {
        setDefectTypes([]);
      }

      // Transform weldLogMetrics array structure into rows
      if (weldLogMetricsData && Array.isArray(weldLogMetricsData) && weldLogMetricsData.length > 0) {
        const mapWeldLogMetricsToTableData = (weldLogMetrics) => {
          if (!Array.isArray(weldLogMetrics)) return [];
          return weldLogMetrics.map((metricsRow, rowIndex) => {
            const row = {};
            row.originalIndex = rowIndex;

            // Ensure unique ID for each row to fix selection issues
            const uniqueId = `weld-row-${rowIndex}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

            Object.defineProperty(row, 'id', {
              value: uniqueId,
              writable: true, // Make it writable for easier state management
              configurable: true,
              enumerable: true
            });

            (Array.isArray(metricsRow) ? metricsRow : []).forEach(metric => {
              if (metric.keyName && metric.keyName !== 'id') {
                const keyName = metric.keyName;
                let value = metric.value;

                // Handle special case for welderId (array field)
                if (keyName === 'welderId' || keyName === 'Welder ID') {
                  if (Array.isArray(value)) {
                    row.welderId = value;
                  } else if (value) {
                    row.welderId = [value];
                  } else {
                    row.welderId = [];
                  }
                }
                // Handle special case for defectInfo (nested array field)
                else if (keyName === 'defectInfo') {
                  // Store the complete defectInfo structure for later use in edit modal
                  if (Array.isArray(value) && value.length > 0) {
                    // Map defectInfo to indications structure
                    row.indications = value.map(defectArray => {
                      const indication = {
                        defectType: '',
                        start: '',
                        end: '',
                        depth: '',
                        length: '',
                        defectAccepted: '',
                        height: ''
                      };

                      if (Array.isArray(defectArray)) {
                        defectArray.forEach(field => {
                          if (field.keyName === 'defectType') indication.defectType = field.value || '';
                          if (field.keyName === 'startLoc') indication.start = field.value || '';
                          if (field.keyName === 'endLoc') indication.end = field.value || '';
                          if (field.keyName === 'depth') indication.depth = field.value || '';
                          if (field.keyName === 'length') indication.length = field.value || '';
                          if (field.keyName === 'defectAccepted') indication.defectAccepted = field.value || '';
                          if (field.keyName === 'height') indication.height = field.value || '';
                        });
                      }

                      return indication;
                    });
                  } else {
                    row.indications = [];
                  }
                }
                // Handle Comments field mappings
                else if (keyName === 'Customercomments' || keyName === 'comments') {
                  row.comments = value || '';
                } else if (keyName === 'crceComments' || keyName === 'crceComment') {
                  row.crceComment = value || '';
                } else if (keyName === 'repairComments' || keyName === 'repairComment') {
                  row.repairComment = value || '';
                }
                // Handle status field - normalize to lowercase
                else if (keyName === 'status') {
                  if (value === 'Accept' || value === 'accept') {
                    row[keyName] = 'accept';
                  } else if (value === 'AcceptWithIndication' || value === 'indication') {
                    row[keyName] = 'indication';
                  } else if (value === 'Reject' || value === 'reject') {
                    row[keyName] = 'reject';
                  } else {
                    row[keyName] = value ? String(value).toLowerCase() : 'accept';
                  }
                }
                // Handle RT field mappings
                else if (keyName === 'rtAcc' || keyName === 'rtAccepted') {
                  row.rtAccepted = (value === '1' || value === 1 || value === true || value === 'true');
                } else if (keyName === 'rtRepairAccepted' || keyName === 'repairAccepted') {
                  row.repairAccepted = (value === '1' || value === 1 || value === true || value === 'true');
                } else if (keyName === 'blowThru' || keyName === 'blowThrough') {
                  row.blowThrough = (value === '1' || value === 1 || value === true || value === 'true');
                } else if (keyName === 'stn1CutOut') {
                  row.stn1CutOut = (value === '1' || value === 1 || value === true || value === 'true');
                } else if (keyName === 'rtCutOut') {
                  row.rtCutOut = (value === '1' || value === 1 || value === true || value === 'true');
                } else if (keyName === 'repair') {
                  row.repair = (value === '1' || value === 1 || value === true || value === 'true');
                } else if (keyName === 'rtRepairRej') {
                  row.rtRepairRej = value || '0';
                } else if (keyName === 'subJoint') {
                  row.subJoint = value || [];
                } else if (keyName === 'jointNo' || keyName === 'Joint No') {
                  row.jointNo = value || '';
                } else if (keyName === 'welderId' || keyName === 'Welder ID') {
                  if (Array.isArray(value)) {
                    row.welderId = value;
                  } else if (value) {
                    row.welderId = [value];
                  } else {
                    row.welderId = [];
                  }
                }
                // Handle other fields normally
                else {
                  if (value === null || value === undefined || value === '') {
                    row[keyName] = metric.type === 'number' ? 0 : '';
                  } else {
                    if (metric.type === 'number') {
                      const numValue = parseFloat(value);
                      row[keyName] = isNaN(numValue) ? 0 : numValue;
                    } else {
                      row[keyName] = String(value);
                    }
                  }
                }
              }
            });

            return row;
          });
        };

        const mappedRows = mapWeldLogMetricsToTableData(weldLogMetricsData);
        setWeldLogRows(mappedRows);
        setWeldEntryRows(mappedRows); // Also update entry rows to stay in sync
        setOriginalWeldLogMetrics(weldLogMetricsData);
        setDeletedRowIndices([]);
      } else {
        setWeldLogRows([]);
        setWeldEntryRows([]);
      }

      // Set notes/remarks
      if (notesData) {
        setRemarks(notesData);
      } else {
        setRemarks('');
      }
    } catch (error) {
      console.error('Error fetching productivity data:', error);
      setWeldLogRows([]);
      setRemarks('');
    } finally {
      setIsLoadingWeldLog(false);
    }
  }, [shift, dprLogId]);

  // Sync initial productivity data if provided as props
  useEffect(() => {
    const data = initialProductivityData && typeof initialProductivityData === 'object' ? initialProductivityData : {};
    if (data.rtProgress && typeof data.rtProgress === 'object') {
      setRtProgress(data.rtProgress);
    }
    if (Array.isArray(data.weldLogRows) && data.weldLogRows.length > 0) {
      setWeldLogRows(data.weldLogRows);
      setWeldEntryRows(data.weldLogRows);
    }
    if (data.remarks != null && data.remarks !== '') {
      setRemarks(data.remarks);
    }
  }, [initialProductivityData]);

  // Refetch data on shift change
  useEffect(() => {
    if (dprLogId) {
      fetchPipeTallyData();
      fetchProductivityData();
    }
  }, [shift, dprLogId, fetchPipeTallyData, fetchProductivityData]);

  // Column name mapping for weldLogMetrics
  const columnNameMap = {
    'date': 'Date',
    'jointNo': 'Joint No',
    'rw': 'RW (Root Weld)',
    'stn1Welds': 'Stage 1 Welds',
    'blowThru': 'Blow Through',
    'stn1CutOut': 'Stage 1 Cut Out / Re-bevel',
    'rtAcc': 'RT Accepted',
    'rtReject': 'RT Rejected',
    'repair': 'Repair',
    'rtRepairRej': 'RT Repair Accepted',
    'rtCutOut': 'RT Cut Out',
    'type': 'Weld Type',
    'startLoc': 'Location',
    'endLoc': 'endLoc',
    'depth': 'Depth (mm)',
    'length': 'Length (mm)',
    'repairComment': 'Comments',
    'welderId': 'CRCE',
    'repairComment1': 'Description'
  };

  // Transform weldEntryRows to metrics format for API payload
  const transformWeldEntryRowsToMetrics = (rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.map((row, rowIndex) => {
      // Helper to get value or default
      const getVal = (val, def = '') => (val !== undefined && val !== null ? String(val) : def);
      const getBoolVal = (val) => (val ? '1' : '0');

      // Map status for API
      let statusVal = 'accept';
      if (row.status === 'indication') statusVal = 'accept';
      if (row.status) statusVal = row.status;

      const metrics = [
        { name: "Date", keyName: "date", value: getVal(row.date, new Date().toISOString().split('T')[0]), weldIndex: rowIndex },
        { name: "Joint No", keyName: "jointNo", value: getVal(row.jointNo), weldIndex: rowIndex },
        { name: "SubJoint", keyName: "subJoint", value: row.subJoint || [], weldIndex: rowIndex },
        { name: "Welder Id", keyName: "welderId", value: Array.isArray(row.welderId) ? row.welderId : (row.welderId ? [row.welderId] : []), weldIndex: rowIndex },
        { name: "Status", keyName: "status", value: statusVal, weldIndex: rowIndex },
        { name: "Station 1 Welds", keyName: "stn1Welds", value: "1", weldIndex: rowIndex },
        { name: "Blow Through", keyName: "blowThru", value: getBoolVal(row.blowThrough), weldIndex: rowIndex },
        { name: "Station 1 Cut Out", keyName: "stn1CutOut", value: getBoolVal(row.stn1CutOut), weldIndex: rowIndex },
        { name: "RT Accepted", keyName: "rtAcc", value: getBoolVal(row.rtAccepted), weldIndex: rowIndex },
        { name: "RT Rejected", keyName: "rtReject", value: row.status === 'reject' ? '1' : '0', weldIndex: rowIndex },
        { name: "Repair", keyName: "repair", value: getBoolVal(row.repair), weldIndex: rowIndex },
        { name: "RT Repair Accepted", keyName: "rtRepairAccepted", value: getBoolVal(row.repairAccepted), weldIndex: rowIndex },
        { name: "RT Repair Rejected", keyName: "rtRepairRej", value: getVal(row.rtRepairRej, '0'), weldIndex: rowIndex },
        { name: "RT Cut Out", keyName: "rtCutOut", value: getBoolVal(row.rtCutOut), weldIndex: rowIndex },

        { name: "Customer Comments", keyName: "comments", value: getVal(row.comments), weldIndex: rowIndex },
        { name: "CRCE Comments", keyName: "crceComment", value: getVal(row.crceComment), weldIndex: rowIndex },
        { name: "Repair Comments", keyName: "repairComment", value: getVal(row.repairComment), weldIndex: rowIndex },
        { name: "RW", keyName: "rw", value: row.repairWeld ? '1' : '0', weldIndex: rowIndex },

        // Defect Info Nested Structure
        {
          id: "16a",
          name: "Defect Info",
          keyName: "defectInfo",
          order: 16.5,
          editable: true,
          type: "array",
          isArray: true,
          weldIndex: rowIndex,
          weldId: row.id || `weld-${rowIndex}`,
          value: (row.indications && row.indications.length > 0) ? row.indications.map(ind => [
            { id: "12", name: "Defect Type", keyName: "defectType", order: 12, editable: true, type: "enum", options: defectTypes || [], value: getVal(ind.defectType) },
            { id: "13", name: "Start Location", keyName: "startLoc", order: 13, editable: true, type: "text", value: getVal(ind.start) },
            { id: "14", name: "End Location", keyName: "endLoc", order: 14, editable: true, type: "text", value: getVal(ind.end) },
            { id: "15", name: "Depth (mm)", keyName: "depth", order: 15, editable: true, type: "text", value: getVal(ind.depth) },
            { id: "16", name: "Length (mm)", keyName: "length", order: 16, editable: true, type: "text", value: getVal(ind.length) },
            { id: "17", name: "Defect Accepted", keyName: "defectAccepted", order: 17, editable: true, type: "text", value: getVal(ind.defectAccepted) },
            { id: "18", name: "Height", keyName: "height", order: 18, editable: true, type: "text", value: getVal(ind.height) }
          ]) : []
        }
      ];

      return metrics;
    });
  };

  // Joint Range Validation
  const validateJointRange = (firstJoint, lastJoint) => {
    const jointRegex = /^[Jj]\d+$/;

    if (!firstJoint) {
      return 'First Joint is required';
    }

    if (!jointRegex.test(firstJoint)) {
      return 'First Joint must start with J/j followed by numbers (e.g., J001, j1)';
    }

    if (lastJoint && !jointRegex.test(lastJoint)) {
      return 'Last Joint must start with J/j followed by numbers (e.g., J005, j5)';
    }

    const firstNum = parseInt(firstJoint.substring(1), 10);

    if (lastJoint) {
      const lastNum = parseInt(lastJoint.substring(1), 10);
      if (lastNum < firstNum) {
        return 'Last Joint number must be greater than or equal to First Joint';
      }
    }

    return '';
  };

  // Generate Joint Rows
  const generateJointRows = (firstJoint, lastJoint) => {
    const firstNum = parseInt(firstJoint.substring(1), 10);
    const padding = firstJoint.substring(1).length;

    if (!lastJoint) {
      return [{
        id: `joint-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        jointNo: firstJoint.toUpperCase(),
        status: 'accept',
        welderId: [],
        selected: false
      }];
    }

    const lastNum = parseInt(lastJoint.substring(1), 10);
    const rows = [];

    for (let i = firstNum; i <= lastNum; i++) {
      const jointNo = `J${String(i).padStart(padding, '0')}`;
      rows.push({
        id: `joint-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
        jointNo,
        status: 'accept',
        welderId: [],
        selected: false
      });
    }

    return rows;
  };

  // Delete Row Handlers
  const handleDeleteRow = (id) => {
    setRowToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setRowToDelete(null);
    setIsBulkDelete(false);
  };

  const handleDeleteSelected = () => {
    setIsBulkDelete(true);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete && !isBulkDelete) return;

    try {
      // Remove the row from state
      // Remove the row from state
      let updatedRows = [];
      if (isBulkDelete) {
        updatedRows = weldEntryRows.filter(row => !row.selected);
      } else {
        updatedRows = weldEntryRows.filter(row => String(row.id) !== String(rowToDelete));
      }
      setWeldEntryRows(updatedRows);
      setIsDeleteModalOpen(false);
      setRowToDelete(null);
      setIsBulkDelete(false);

      // Immediately call PUT API with updated data (excluding deleted row)
      if (dprLogId && updatedRows.length >= 0) {
        setIsSaving(true);
        try {
          // Transform updated rows to API format
          const weldLogMetrics = transformWeldEntryRowsToMetrics(updatedRows);

          const payload = {
            shift: shift,
            dprProductivity: {
              dprLogId: dprLogId,
              reportNo: reportNo || `DPR-${dprLogId}-P-v1`,
              notes: remarks,
              weldLogMetrics: weldLogMetrics
            }
          };

          await axiosConfig.put('/dpr-productivity', payload);
          message.success('Weld entry deleted successfully');

          // Refetch data to update UI
          await fetchProductivityData();
          await fetchPipeTallyData();
        } catch (error) {
          console.error('Error deleting weld entry:', error);
          message.error('Failed to delete weld entry');
          // Revert state on error
          setWeldEntryRows(weldEntryRows);
        } finally {
          setIsSaving(false);
        }
      }
    } catch (error) {
      console.error('Error in delete confirmation:', error);
    }
  };

  const handleSave = async () => {
    if (!dprLogId) {
      message.error('DPR Log ID is required to save');
      return;
    }

    // Validation
    const invalidRows = weldEntryRows.filter(row => !row.jointNo || !row.welderId || (Array.isArray(row.welderId) && row.welderId.length === 0) || !row.status || !row.comments || !row.crceComment);
    if (invalidRows.length > 0) {
      message.error(`Please fill in mandatory fields (Joint No, Welder ID, Weld Status, Comments, CRCE Comment) for all rows.`);
      return;
    }


    // RT status validations removed - no longer enforcing specific RT field values based on status

    setIsSaving(true);
    try {
      const testTypesList = testTypes || [];
      const rtTestType = testTypesList.find(test => test.testType === 'RT') || testTypesList[0];
      const firstRtWeld = rtProgress.firstRtWeld || '';
      const lastRtWeld = rtProgress.lastRtWeld || '';
      const totalRtAcceptedWelds = rtProgress.totalRtAcceptedWelds || 0;

      // Use transformWeldEntryRowsToMetrics instead of the old function
      // and pass weldEntryRows (the edited state)
      const weldLogMetrics = transformWeldEntryRowsToMetrics(weldEntryRows);

      const payload = {
        shift: shift,
        dprProductivity: {
          dprLogId: dprLogId,
          // reportNo: reportNo || `DPR-${dprLogId}-P-v1`, // Clean this up if not needed or confirm source
          firstRtWeld: firstRtWeld,
          lastRtWeld: lastRtWeld,
          totalRtAcceptedWelds: Number(totalRtAcceptedWelds),
          weldLogMetrics: weldLogMetrics,
          notes: remarks || ''
        }
      };

      console.log("Saving Payload:", JSON.stringify(payload, null, 2));

      await axiosConfig.put('/dpr-productivity', payload);

      message.success('Productivity report saved successfully');

      await fetchProductivityData();
      await fetchPipeTallyData();

      if (onSave) {
        onSave({
          rtProgress,
          weldLogRows, // This might need to be updated with new rows? Fetch will update it.
          remarks
        });
      }
    } catch (error) {
      console.error('Error saving productivity report:', error);
      message.error('Failed to save productivity report');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddProductivity = () => {
    setIsAddModalVisible(true);
    setNewProductivity({
      firstJoint: '',
      lastJoint: ''
    });
    setValidationError('');
  };

  const handleAddModalCancel = () => {
    setIsAddModalVisible(false);
    setNewProductivity({
      firstJoint: '',
      lastJoint: ''
    });
    setValidationError('');
  };

  const handleAddRecords = () => {
    const error = validateJointRange(newProductivity.firstJoint, newProductivity.lastJoint);

    if (error) {
      setValidationError(error);
      return;
    }

    const generatedRows = generateJointRows(newProductivity.firstJoint, newProductivity.lastJoint);

    // Filter duplicates
    const existingJoints = new Set(weldEntryRows.map(row => (row.jointNo || '').toUpperCase()));
    const newRows = generatedRows.filter(row => !existingJoints.has(row.jointNo.toUpperCase()));

    if (newRows.length === 0) {
      message.warning('All joint numbers in this range already exist.');
      handleAddModalCancel(); // Close modal anyway? Or keep open? Usually close to reset.
      return;
    }

    setWeldEntryRows((prev) => [...prev, ...newRows]);
    handleAddModalCancel();

    if (newRows.length < generatedRows.length) {
      message.success(`Added ${newRows.length} new records (${generatedRows.length - newRows.length} duplicates skipped)`);
    } else {
      message.success(`${newRows.length} weld record(s) added successfully`);
    }
  };

  const handleDiscardChanges = () => {
    setWeldEntryRows([]);
    setSelectAll(false);
    message.info('Changes discarded');
  };

  const handleSelectAllRows = (checked) => {
    setSelectAll(checked);
    setWeldEntryRows((rows) => (Array.isArray(rows) ? rows : []).map((row) => ({ ...row, selected: checked })));
  };

  const handleSelectRow = (id, checked) => {
    setWeldEntryRows((rows) => {
      const list = Array.isArray(rows) ? rows : [];
      const updated = list.map((row) =>
        row.id === id ? { ...row, selected: checked } : row
      );
      setSelectAll(updated.every((row) => row.selected));
      return updated;
    });
  };

  const handleStatusChange = (id, status) => {
    setWeldEntryRows((rows) =>
      (Array.isArray(rows) ? rows : []).map((row) => (row.id === id ? { ...row, status } : row))
    );
  };

  const handleWelderIdChange = (id, welderId) => {
    setWeldEntryRows((rows) =>
      (Array.isArray(rows) ? rows : []).map((row) => (row.id === id ? { ...row, welderId: Array.isArray(welderId) ? welderId : [welderId] } : row))
    );
  };

  const handleEditRow = (row) => {
    setEditingRow(row);
  };

  const handleUpdateRow = (updatedRow) => {
    console.log("Updating row:", updatedRow);
    setWeldEntryRows((rows) => {
      const updated = (Array.isArray(rows) ? rows : []).map((row) =>
        String(row.id) === String(updatedRow.id) ? { ...updatedRow } : row
      );
      console.log("New rows after update:", updated);
      return updated;
    });
    setEditingRow(null);
    message.success('Weld entry updated successfully');
  };

  const uploadProps = {
    accept: '.xlsx,.xls',
    multiple: false,
    fileList: uploadFileList,
    beforeUpload: (file) => {
      const isExcelType =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel';
      const isExcelExt = /\.(xlsx|xls)$/i.test(file.name || '');
      const isExcel = isExcelType || isExcelExt;

      if (!isExcel) {
        message.error('Please upload a valid Excel file (.xls or .xlsx).');
        return Upload.LIST_IGNORE;
      }

      setUploadFileList([file]);
      return false;
    },
    onRemove: () => {
      setUploadFileList([]);
    }
  };

  // Safe arrays for render (guard against undefined from props/state)
  const safeTestTypes = Array.isArray(testTypes) ? testTypes : [];
  const safeWeldEntryRows = Array.isArray(weldEntryRows) ? weldEntryRows : [];

  // Columns for template download/processing
  const columns = [
    { title: '#', key: 'index', dataIndex: 'index' },
    { title: 'Joint No', dataIndex: 'jointNo', key: 'jointNo' },
    { title: 'Rw', dataIndex: 'rw', key: 'rw' },
    { title: 'STN1 Welds', dataIndex: 'stn1Welds', key: 'stn1Welds' },
    { title: 'BLOW THRU', dataIndex: 'blowThru', key: 'blowThru' },
    { title: 'STN 1 Cut Out', dataIndex: 'stn1CutOut', key: 'stn1CutOut' },
    { title: 'RT ACC', dataIndex: 'rtAcc', key: 'rtAcc' },
    { title: 'RT Reject', dataIndex: 'rtReject', key: 'rtReject' },
    { title: 'REPAIR', dataIndex: 'repair', key: 'repair' },
    { title: 'RT Repair REJ', dataIndex: 'rtRepairRej', key: 'rtRepairRej' },
    { title: 'RT Cut Out', dataIndex: 'rtCutOut', key: 'rtCutOut' },
    { title: 'Type', dataIndex: 'type', key: 'type' },
    { title: 'Start Loc', dataIndex: 'startLoc', key: 'startLoc' },
    { title: 'End Loc', dataIndex: 'endLoc', key: 'endLoc' },
    { title: 'Depth', dataIndex: 'depth', key: 'depth' },
    { title: 'Length', dataIndex: 'length', key: 'length' },
    { title: 'Welder ID', dataIndex: 'welderId', key: 'welderId' },
    { title: 'Repair Comment', dataIndex: 'repairComment', key: 'repairComment' },
    { title: 'Action', key: 'action' }
  ];

  if (editingRow) {
    return (
      <EditWeldEntry
        entry={editingRow}
        onSave={handleUpdateRow}
        onCancel={() => setEditingRow(null)}
        welderOptions={welderPersonnel}
        defectTypes={defectTypes}
      />
    );
  }

  return (
    <div style={{
      padding: 0,
      minHeight: '100vh',
      backgroundColor: '#F8FAFC',
      textAlign: 'left',
      position: 'relative'
    }}>
      {(isLoadingWeldLog || isSaving || isLoadingPipeTally) && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 1000
        }}>
          <Spinner variant="inline" size="medium" />
        </div>
      )}
      <style>
        {`
        .productivity-input {
          background-color: #f9fafb !important;
          border-color: #e5e7eb !important;
          color: #111827 !important;
          border-radius: 6px !important;
        }
        
        .productivity-input:focus {
          border-color: #7c3aed !important;
          box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.2) !important;
          background-color: #fff !important;
        }

        .productivity-input::placeholder {
          color: #9ca3af !important;
        }
        
        .qty-suffix {
          font-weight: 500;
          font-size: 12px;
        }

        .rt-total-input {
          background-color: #ffffff !important;
          border-color: #e5e7eb !important;
          color: #111827 !important;
          font-weight: 600 !important;
          border-radius: 6px !important;
        }
        
        .rt-total-input input {
          color: #111827 !important;
          font-weight: 600 !important;
        }


        .weld-entry-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          background-color: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          overflow: hidden;
        }

        .weld-entry-table th {
          background-color: #EF4444;
          color: white;
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          padding: 12px 16px;
          text-align: left;
          letter-spacing: 0.05em;
        }

        .weld-entry-table td {
          padding: 14px 16px;
          border-bottom: 1px solid #F3F4F6;
          background-color: white;
          font-size: 14px;
          color: #1F2937;
        }

        .weld-entry-table tbody tr:last-child td {
          border-bottom: none;
        }

        .weld-entry-table tbody tr:hover td {
          background-color: #F9FAFB;
        }

        .status-icon-circle {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justifyContent: center;
          cursor: pointer;
          transition: all 0.15s ease;
          border: 2px solid transparent;
        }

        .status-icon-circle:hover {
          transform: scale(1.08);
        }

        .status-icon-circle.accept {
          background-color: transparent;
          border-color: #10B981;
        }

        .status-icon-circle.accept.active {
          background-color: #10B981;
        }

        .status-icon-circle.indication {
          background-color: transparent;
          border-color: #F59E0B;
        }

        .status-icon-circle.indication.active {
          background-color: #F59E0B;
        }

        .status-icon-circle.reject {
          background-color: transparent;
          border-color: #EF4444;
        }

        .status-icon-circle.reject.active {
          background-color: #EF4444;
        }

        .action-icon-btn {
          cursor: pointer;
          color: #9CA3AF;
          transition: color 0.2s;
          padding: 4px;
          border-radius: 4px;
        }

        .action-icon-btn:hover {
          color: #EF4444;
          background-color: #FEE2E2;
        }
        `}
      </style>

      {/* Main Content Card */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        maxWidth: '100%'
      }}>
        {/* Header with Icon */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: '#F3F4F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <BarChartOutlined style={{ color: '#4B5563', fontSize: '20px' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{
                margin: '0 0 4px 0',
                color: '#111827',
                fontSize: '20px',
                fontWeight: 600,
                lineHeight: '28px'
              }}>
                Productivity Log
              </h2>
              <p style={{
                margin: 0,
                color: '#6B7280',
                fontSize: '14px',
                lineHeight: '20px'
              }}>
                Track daily production metrics and weld outputs
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Checkbox
                label="Skip Productivity"
                checked={skipProductivity}
                onChange={(checked) => sowId && dispatch(updateDprSkipState(sowId, 'skipProductivity', checked))}
                borderColor="#00C9A7"
              />
              <CrcButton
                label="Add Weld"
                variant="primary"
                size="small"
                icon={Plus}
                onClick={handleAddProductivity}
                style={{ backgroundColor: '#F43F5E', borderColor: '#F43F5E' }}
              />
            </div>
          </div>
        </div>

        {/* RT Progress Section */}
        <div style={{ marginTop: '32px', marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid #E5E7EB' }}>
          {safeTestTypes.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
              No test types available.
            </div>
          ) : (
            safeTestTypes.map((testType, index) => (
              <div key={testType.testType || index} style={{ marginBottom: index < safeTestTypes.length - 1 ? '32px' : '0' }}>
                <h3 style={{
                  margin: '0 0 20px 0',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {String(testType.testLabel || testType.testType).toUpperCase()} PROGRESS
                </h3>

                <Row gutter={24}>
                  <Col xs={24} sm={8}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#64748B', marginBottom: '8px' }}>
                      FIRST RT WELD
                    </div>
                    <Input
                      value={testType.firstWeldNo || ''}
                      placeholder="e.g., W-001"
                      className="productivity-input"
                      readOnly
                      size="large"
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#64748B', marginBottom: '8px' }}>
                      LAST RT WELD
                    </div>
                    <Input
                      value={testType.lastWeldNo || ''}
                      placeholder="e.g., W-012"
                      className="productivity-input"
                      readOnly
                      size="large"
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#64748B', marginBottom: '8px' }}>
                      TOTAL RT ACCEPTED WELDS
                    </div>
                    <Input
                      value={testType.totalCompleted || 0}
                      className="rt-total-input"
                      readOnly
                      size="large"
                      suffix={<span className="qty-suffix">Qty</span>}
                      style={{ textAlign: 'left', color: '#111827', fontWeight: '600' }}
                    />
                  </Col>
                </Row>
              </div>
            ))
          )}
        </div>

        {/* Weld Entry Table Section */}
        {safeWeldEntryRows.length > 0 && (
          <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid #E5E7EB' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '4px',
                  height: '18px',
                  backgroundColor: '#EF4444',
                  borderRadius: '2px'
                }} />
                <h3 style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1F2937',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em'
                }}>
                  DAILY WELD LOG
                </h3>
              </div>

              {(Array.isArray(weldEntryRows) ? weldEntryRows : []).some(r => r.selected) && (
                <CrcButton
                  label={`Delete Selected (${(Array.isArray(weldEntryRows) ? weldEntryRows : []).filter(r => r.selected).length})`}
                  variant="tertiary"
                  size="small"
                  icon={Trash2}
                  onClick={handleDeleteSelected}
                  style={{
                    color: '#EF4444',
                    borderColor: '#EF4444',
                    backgroundColor: '#FEF2F2'
                  }}
                />
              )}
            </div>

            {/* Status Legend with Icons */}
            <div style={{ marginBottom: '16px', display: 'flex', gap: '20px', fontSize: '12px', color: '#64748B' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle size={16} color="#10B981" fill="transparent" />
                <span>Accept</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertCircle size={16} color="#F59E0B" fill="transparent" />
                <span>Accept w/ Indication</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <XCircle size={16} color="#EF4444" fill="transparent" />
                <span>Reject</span>
              </div>
            </div>

            {/* Header Row with Select All */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ width: '60px', display: 'flex', justifyContent: 'center' }}>
                <AntCheckbox
                  checked={selectAll}
                  onChange={(e) => handleSelectAllRows(e.target.checked)}
                />
              </div>
              <div style={{ width: '20%', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                JOINT NO
              </div>
              <div style={{ width: '30%', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                STATUS
              </div>
              <div style={{ width: '30%', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                WELDER ID
              </div>
              <div style={{ width: '10%', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                ACTION
              </div>
            </div>

            {/* Card List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {safeWeldEntryRows.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#94A3B8' }}>
                  No weld entries added.
                </div>
              ) : (
                safeWeldEntryRows.map((row) => (
                  <div
                    key={row.id}
                    onMouseEnter={() => setHoveredRow(row.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '16px',
                      border: hoveredRow === row.id ? '1px solid #00C9A7' : '1px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      transition: 'border-color 0.2s ease'
                    }}
                  >
                    {/* Checkbox */}
                    <div style={{ width: '60px', display: 'flex', justifyContent: 'center' }}>
                      <AntCheckbox
                        checked={row.selected}
                        onChange={(e) => handleSelectRow(row.id, e.target.checked)}
                      />
                    </div>

                    {/* Joint Number */}
                    <div style={{ width: '20%', fontWeight: 600, color: '#111827', fontSize: '14px' }}>
                      {row.jointNo}
                    </div>

                    {/* Status Icons */}
                    <div style={{ width: '30%', display: 'flex', justifyContent: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '10px' }}>
                        <div
                          onClick={() => handleStatusChange(row.id, 'accept')}
                          title="Accept"
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <CheckCircle
                            size={22}
                            color={row.status === 'accept' ? '#059669' : '#10B981'}
                            fill={row.status === 'accept' ? '#D1FAE5' : 'transparent'}
                          />
                        </div>
                        <div
                          onClick={() => handleStatusChange(row.id, 'indication')}
                          title="Accept with Indication"
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <AlertCircle
                            size={22}
                            color={row.status === 'indication' ? '#D97706' : '#F59E0B'}
                            fill={row.status === 'indication' ? '#FEF3C7' : 'transparent'}
                          />
                        </div>
                        <div
                          onClick={() => handleStatusChange(row.id, 'reject')}
                          title="Reject"
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <XCircle
                            size={22}
                            color={row.status === 'reject' ? '#DC2626' : '#EF4444'}
                            fill={row.status === 'reject' ? '#FEE2E2' : 'transparent'}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Welder ID Dropdown (Custom Multi-select) */}
                    <div style={{ width: '30%' }}>
                      <CustomWelderSelect
                        placeholder="Select Welders"
                        value={row.welderId}
                        options={welderPersonnel}
                        onChange={(vals) => handleWelderIdChange(row.id, vals)}
                      />
                    </div>

                    {/* Actions - Edit and Delete */}
                    <div style={{ width: '10%', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          handleEditRow(row);
                        }}
                        title="Edit"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#D1D5DB',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#3B82F6';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#D1D5DB';
                        }}
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteRow(row.id)}
                        title="Delete"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#D1D5DB',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#EF4444';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#D1D5DB';
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Overall Comments Section */}
        <div style={{ marginBottom: '32px' }}>
          <h4 style={{
            margin: '0 0 12px 0',
            fontSize: '14px',
            fontWeight: 500,
            color: '#64748B',
            textAlign: 'left'
          }}>
            OVERALL COMMENTS
          </h4>
          <TextArea
            rows={6}
            placeholder="Add overall report notes or comments here..."
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            className="productivity-input"
            style={{ padding: '12px', resize: 'vertical' }}
          />
        </div>

        {/* Action Buttons */}
        <div style={{
          position: 'sticky',
          bottom: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '24px',
          paddingTop: '16px',
          zIndex: 10,
        }}>
          <CrcButton
            label="Save"
            variant="secondary"
            onClick={handleSave}
            disabled={isSubmitting || isSaving}
            style={{
              height: '42px',
              padding: '0 32px',
              fontSize: '14px',
              fontWeight: 600
            }}
            icon={SaveOutlined}
          />
        </div>
      </div>

      {/* Upload Modal */}
      <Modal
        title="Upload Excel"
        open={isUploadModalVisible}
        onOk={handleUploadExcelFile}
        onCancel={handleUploadModalCancel}
        okText="Upload"
      >
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ color: '#7c3aed' }} />
          </p>
          <p className="ant-upload-text">Click or drag Excel file to this area</p>
          <p className="ant-upload-hint">Only .xls or .xlsx files are supported.</p>
        </Upload.Dragger>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        title={isBulkDelete ? "Delete Selected Entries" : "Delete Weld Entry"}
        message={isBulkDelete
          ? `Are you sure you want to delete these ${(Array.isArray(weldEntryRows) ? weldEntryRows : []).filter(r => r.selected).length} selected weld entries? This action cannot be undone.`
          : "Are you sure you want to delete this weld entry? This action cannot be undone."
        }
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isOpen={isDeleteModalOpen}
        size="medium"
        confirmButtonProps={{
          style: {
            backgroundColor: '#FF3B30',
            borderColor: '#FF3B30'
          }
        }}
      />

      {/* Add Productivity Modal */}
      {isAddModalVisible && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '100px',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            width: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                  Add Weld Records
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
                  Configure joint range and pre-fill common values.
                </p>
              </div>
              <button
                onClick={handleAddModalCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <div style={{
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid #E5E7EB',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#000000',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  JOINT RANGE
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                      textAlign: 'left'
                    }}>
                      First Joint
                    </label>
                    <input
                      type="text"
                      value={newProductivity.firstJoint}
                      onChange={(e) => {
                        setNewProductivity({ ...newProductivity, firstJoint: e.target.value });
                        setValidationError('');
                      }}
                      placeholder="e.g., J001"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${validationError ? '#EF4444' : '#D1D5DB'}`,
                        fontSize: '14px',
                        outline: 'none',
                        color: '#1F2937',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = validationError ? '#EF4444' : '#4F46E5'}
                      onBlur={(e) => e.target.style.borderColor = validationError ? '#EF4444' : '#D1D5DB'}
                    />
                  </div>

                  <div style={{ paddingTop: '28px', color: '#9CA3AF' }}>
                    <ArrowRight size={20} />
                  </div>

                  <div style={{ flex: 1 }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                      textAlign: 'left'
                    }}>
                      Last Joint (Opt)
                    </label>
                    <input
                      type="text"
                      value={newProductivity.lastJoint}
                      onChange={(e) => {
                        setNewProductivity({ ...newProductivity, lastJoint: e.target.value });
                        setValidationError('');
                      }}
                      placeholder="e.g., J010"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${validationError ? '#EF4444' : '#D1D5DB'}`,
                        fontSize: '14px',
                        outline: 'none',
                        color: '#1F2937',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = validationError ? '#EF4444' : '#4F46E5'}
                      onBlur={(e) => e.target.style.borderColor = validationError ? '#EF4444' : '#D1D5DB'}
                    />
                  </div>
                </div>

                {/* Validation Error */}
                {validationError && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#EF4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <XCircle size={12} />
                    <span>{validationError}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              backgroundColor: '#ffffff',
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginTop: 'auto'
            }}>
              <CrcButton
                label="Cancel"
                variant="tertiary"
                onClick={handleAddModalCancel}
                size="medium"
                style={{ width: '48%', borderRadius: '24px', border: '1px solid #E5E7EB' }}
              />
              <CrcButton
                label="Add Records"
                variant="primary"
                onClick={handleAddRecords}
                size="medium"
                style={{
                  width: '48%',
                  backgroundColor: '#EF4444',
                  borderColor: '#EF4444',
                  borderRadius: '24px'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// EditWeldEntry Component
const EditWeldEntry = ({ entry, onSave, onCancel, welderOptions, defectTypes }) => {
  // Helper function to get default RT field values based on status
  const getDefaultRTFields = (status, entry) => {
    // Check if RT fields are already explicitly set in the entry
    const hasExistingRTValues =
      entry.rtAccepted !== undefined ||
      entry.repair !== undefined ||
      entry.repairAccepted !== undefined ||
      entry.rtCutOut !== undefined;

    // If RT fields already exist, use them as-is
    if (hasExistingRTValues) {
      return {
        rtAccepted: entry.rtAccepted || false,
        repair: entry.repair || false,
        repairAccepted: entry.repairAccepted || false,
        rtCutOut: entry.rtCutOut || false
      };
    }

    // For new entries, always default to "No" (false) for all RT fields
    return {
      rtAccepted: false,
      repair: false,
      repairAccepted: false,
      rtCutOut: false
    };
  };

  const defaultRTFields = getDefaultRTFields(entry.status, entry);

  const [formData, setFormData] = useState({
    ...entry,
    repairWeld: entry.repairWeld || false,
    blowThrough: entry.blowThrough || false,
    stn1CutOut: entry.stn1CutOut || false,
    ...defaultRTFields,
    indications: (entry.indications && entry.indications.length > 0) ? entry.indications : [{ defectType: '', start: '', end: '', depth: '', length: '' }]
  });

  const [errors, setErrors] = useState({});

  const toggleField = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleIndicationChange = (index, field, value) => {
    let finalValue = value;

    // Strict numeric validation for location and dimension fields
    if (['start', 'end', 'depth', 'length'].includes(field)) {
      finalValue = value.replace(/[^0-9.]/g, ''); // Allow only digits and decimal point
      // Ensure only one decimal point
      const parts = finalValue.split('.');
      if (parts.length > 2) {
        finalValue = parts[0] + '.' + parts.slice(1).join('');
      }
    }

    const newIndications = [...(formData.indications || [])];
    newIndications[index] = { ...newIndications[index], [field]: finalValue };
    setFormData(prev => ({ ...prev, indications: newIndications }));
  };

  const addIndication = () => {
    setFormData(prev => ({
      ...prev,
      indications: [...(prev.indications || []), { defectType: '', start: '', end: '', depth: '', length: '' }]
    }));
  };

  const removeIndication = (index) => {
    setFormData(prev => ({
      ...prev,
      indications: (prev.indications || []).filter((_, i) => i !== index)
    }));
  };

  return (

    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh', padding: '0px' }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        maxWidth: '100%',
        minHeight: 'calc(100vh - 64px)',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '4px', height: '24px', backgroundColor: '#EF4444', borderRadius: '2px' }} />
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827' }}>
              {formData.jointNo} - WELD LOG
            </h2>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={24} color="#6B7280" />
          </button>
        </div>

        {/* Identity & Location */}
        <div style={{ marginBottom: '32px', borderBottom: '1px solid #F3F4F6', paddingBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 16px 0', textAlign: 'left' }}>• Identity & Location</h3>
          <div style={{ display: 'flex', gap: '54px', flexWrap: 'wrap' }}>
            <div style={{ flex: 2, minWidth: '300px' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                textAlign: 'left'
              }}>
                Joint No <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                value={formData.jointNo}
                onChange={(e) => setFormData(prev => ({ ...prev, jointNo: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #D1D5DB',
                  fontSize: '14px',
                  outline: 'none',
                  color: '#1F2937',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                onBlur={(e) => e.target.style.borderColor = errors.jointNo ? '#EF4444' : '#D1D5DB'}
              />
              {errors.jointNo && <span style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.jointNo}</span>}
            </div>
            <div style={{ flex: 2, minWidth: '300px' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                textAlign: 'left'
              }}>
                Welder ID <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <CustomWelderSelect
                placeholder="Select Welders"
                value={formData.welderId}
                options={welderOptions}
                onChange={(vals) => {
                  setFormData(prev => ({ ...prev, welderId: vals }));
                  if (errors.welderId) setErrors(prev => ({ ...prev, welderId: null }));
                }}
              />
              {errors.welderId && <span style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.welderId}</span>}
            </div>
            <div style={{ flex: 1, minWidth: '150px' }}>
            </div>
          </div>
        </div>

        {/* Weld Status */}
        <div style={{ marginBottom: '32px', borderBottom: '1px solid #F3F4F6', paddingBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 16px 0', textAlign: 'left' }}>• Weld Status <span style={{ color: 'red' }}>*</span></h3>
          <div style={{ display: 'flex', backgroundColor: '#F3F4F6', borderRadius: '30px', padding: '4px' }}>
            {['accept', 'indication', 'reject'].map(status => (
              <div
                key={status}
                onClick={() => {
                  // Only update the status, no default RT field values
                  setFormData(prev => ({ ...prev, status }));
                }}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '10px',
                  borderRadius: '26px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '14px',
                  backgroundColor: formData.status === status ? (
                    status === 'accept' ? '#14B8A6' :
                      status === 'indication' ? '#F59E0B' :
                        status === 'reject' ? '#EF4444' : 'white'
                  ) : 'transparent',
                  color: formData.status === status ? 'white' : '#6B7280',
                  boxShadow: formData.status === status ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                {status === 'accept' && 'Accept'}
                {status === 'indication' && 'Accept w/ Indication'}
                {status === 'reject' && (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    Reject
                  </span>
                )}
              </div>
            ))}
          </div>
          {errors.status && <span style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.status}</span>}
        </div>

        {/* Technical Evidence */}
        <div style={{ marginBottom: '32px', borderBottom: '1px solid #F3F4F6', paddingBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            {/* Icon or visual for Technical Evidence if needed, header style text */}
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#9CA3AF' }}>Technical Evidence</span>
          </div>

          <div style={{ borderLeft: '3px solid #EF4444', paddingLeft: '12px', marginBottom: '16px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#1F2937', textAlign: 'left' }}>Weld Process Stats</h4>
          </div>

          <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <ToggleField
              label="Blow Through"
              value={formData.blowThrough}
              onChange={() => toggleField('blowThrough')}
              activeColor="#EF4444"
              inactiveColor="#14B8A6"
            />
            <ToggleField
              label="Station 1 Cut Out"
              value={formData.stn1CutOut}
              onChange={() => toggleField('stn1CutOut')}
              activeColor="#EF4444"
              inactiveColor="#14B8A6"
            />
          </div>

          <div style={{ borderLeft: '3px solid #EF4444', paddingLeft: '12px', marginBottom: '16px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#1F2937', textAlign: 'left' }}>Radiographic Testing (RT)</h4>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px' }}>
            <ToggleField label="RT Accepted" value={formData.rtAccepted} onChange={() => toggleField('rtAccepted')} inactiveColor="#EF4444" />
            <ToggleField label="Repair" value={formData.repair} onChange={() => toggleField('repair')} inactiveColor="#EF4444" />
            <ToggleField label="Repair Accepted" value={formData.repairAccepted} onChange={() => toggleField('repairAccepted')} inactiveColor="#EF4444" />
            <ToggleField label="RT Cut Out" value={formData.rtCutOut} onChange={() => toggleField('rtCutOut')} inactiveColor="#EF4444" />
          </div>
          {errors.rtConsistency && <div style={{ color: '#EF4444', fontSize: '12px', marginTop: '8px' }}>{errors.rtConsistency}</div>}
        </div>

        {/* Indication Info */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: 0 }}>• Indication Info</h3>
            <CrcButton
              label="Add Indication"
              icon={Plus}
              variant="tertiary"
              size="small"
              onClick={addIndication}
              style={{ color: '#3B82F6', borderColor: '#3B82F6' }}
            />
          </div>

          {(formData.indications || []).map((indication, index) => (
            <div key={index} style={{ backgroundColor: '#F9FAFB', padding: '16px', borderRadius: '8px', marginBottom: '16px', position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#1F2937' }}>Indication #{index + 1}</span>
                <button onClick={() => removeIndication(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}>
                  <Trash2 size={16} />
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Defect Type
                  </label>
                  <select
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box',
                      backgroundColor: '#fff',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    value={indication.defectType}
                    onChange={(e) => handleIndicationChange(index, 'defectType', e.target.value)}
                  >
                    <option value="">Select Defect Type</option>
                    {defectTypes && defectTypes.length > 0 ? (
                      defectTypes.map(defect => (
                        <option key={defect.code} value={defect.code}>
                          {defect.code} - {defect.description}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="IFS">IFS - Incomplete Fusion</option>
                        <option value="POR">POR - Porosity</option>
                        <option value="SLG">SLG - Slag Inclusion</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Start Location(mm)
                  </label>
                  <input
                    value={indication.start}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    onChange={(e) => handleIndicationChange(index, 'start', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    End Location(mm)
                  </label>
                  <input
                    value={indication.end}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    onChange={(e) => handleIndicationChange(index, 'end', e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Depth (mm)
                  </label>
                  <input
                    value={indication.depth}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    onChange={(e) => handleIndicationChange(index, 'depth', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Length (mm)
                  </label>
                  <input
                    value={indication.length}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    onChange={(e) => handleIndicationChange(index, 'length', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 16px 0', textAlign: 'left', textTransform: 'uppercase' }}>• Additional Information</h3>

          <div style={{ backgroundColor: '#F9FAFB', padding: '16px', borderRadius: '8px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '54px', marginBottom: '24px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Comments <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  value={formData.comments || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, comments: e.target.value }));
                    if (errors.comments) setErrors(prev => ({ ...prev, comments: null }));
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#1F2937',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                  onBlur={(e) => e.target.style.borderColor = errors.comments ? '#EF4444' : '#D1D5DB'}
                />
                {errors.comments && <span style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.comments}</span>}
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  CRCE Comment <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  value={formData.crceComment || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, crceComment: e.target.value }));
                    if (errors.crceComment) setErrors(prev => ({ ...prev, crceComment: null }));
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#1F2937',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                  onBlur={(e) => e.target.style.borderColor = errors.crceComment ? '#EF4444' : '#D1D5DB'}
                />
                {errors.crceComment && <span style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.crceComment}</span>}
              </div>
            </div>


            <CommentBox
              header="Repair Comment"
              value={formData.repairComment || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, repairComment: e.target.value }))}
              placeholder="Enter repair comments..."
            />
          </div>
        </div>





        {/* Footer Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '24px 0',
          marginTop: 'auto',
          position: 'sticky',
          bottom: '0',
          backgroundColor: '#FFFFFF',
          zIndex: 20
        }}>
          <CrcButton
            label="Cancel"
            variant="tertiary"
            onClick={onCancel}
            style={{ height: '42px', padding: '0 24px', fontSize: '14px', fontWeight: 600 }}
          />
          <CrcButton
            label="Save"
            variant="secondary"
            onClick={() => {
              const newErrors = {};

              // Mandatory Fields
              if (!formData.jointNo) newErrors.jointNo = 'Joint No is required';
              if (!formData.welderId || (Array.isArray(formData.welderId) && formData.welderId.length === 0)) newErrors.welderId = 'Welder ID is required';
              if (!formData.status) newErrors.status = 'Weld Status is required';
              if (!formData.comments) newErrors.comments = 'Comments is required';
              if (!formData.crceComment) newErrors.crceComment = 'CRCE Comment is required';


              // No validation for toggle fields (Blow Through, Station 1 Cut Out, RT fields)

              if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                message.error('Please resolve validation errors.');
                return;
              }

              // clear errors and save
              setErrors({});
              onSave(formData);
            }}
            style={{ height: '42px', padding: '0 32px', fontSize: '14px', fontWeight: 600 }}
            icon={SaveOutlined}
          />
        </div>
      </div>
    </div>
  );
};

const ToggleField = ({ label, value, onChange, activeColor = '#14B8A6', inactiveColor = '#0F172A' }) => (
  <div style={{ flex: 1, minWidth: '200px' }}>
    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#6B7280', marginBottom: '8px', textAlign: 'left' }}>{label}</label>
    <div style={{ display: 'flex', border: '1px solid #E5E7EB', borderRadius: '24px', padding: '2px', backgroundColor: 'white' }}>
      <div
        onClick={() => value && onChange()}
        style={{
          flex: 1,
          textAlign: 'center',
          padding: '8px',
          borderRadius: '20px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          backgroundColor: !value ? inactiveColor : 'transparent',
          color: !value ? 'white' : '#6B7280'
        }}
      >
        No
      </div>
      <div
        onClick={() => !value && onChange()}
        style={{
          flex: 1,
          textAlign: 'center',
          padding: '8px',
          borderRadius: '20px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          backgroundColor: value ? activeColor : 'transparent',
          color: value ? 'white' : '#6B7280'
        }}
      >
        Yes
      </div>
    </div>
  </div>
);

// Local Component: CommentBox
const CommentBox = ({ header, value, onChange, placeholder }) => (
  <div style={{ marginTop: '0px' }}>
    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500, color: '#64748B', textAlign: 'left' }}>
      {header}
    </h4>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={6}
      style={{
        width: '100%',
        padding: '16px',
        fontSize: '15px',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        color: '#1F2937',
        backgroundColor: '#FFFFFF'
      }}
    />
  </div>
);

export default ProductivityLog;
